import React from 'react';
import { FormGroup, FormField, TextArea } from 'semantic-ui-react';

import { QUESTIONS, TRIAGE_QUESTION_ID } from '../fixtures';

import type { SharedProps } from './shared';

/**
 * Member received path: 54 → 55 → (55a/55b/56a/56b/57) → 58 → 59 → 60 → 61 → 62
 */
export const ReceivedPathSection: React.FC<SharedProps> = ({
  handleQuestion,
  handleTextInput,
  getAnswer,
  textInputs,
}) => {
  const q05b = getAnswer(TRIAGE_QUESTION_ID.RECEIVED_DAMAGE_TYPE);
  const q34 = getAnswer(TRIAGE_QUESTION_ID.PERCENT_IMPACTED);
  const q55a = getAnswer(TRIAGE_QUESTION_ID.IS_MF_ORDER);
  const q37 = getAnswer(TRIAGE_QUESTION_ID.WILL_REPLACE_ORDER);
  const q08 = getAnswer(TRIAGE_QUESTION_ID.BONUS_PACK);
  const q35 = getAnswer(TRIAGE_QUESTION_ID.SEVEN_DAY_OVERRIDE);
  const q38 = getAnswer(TRIAGE_QUESTION_ID.VENDOR_REFUND);
  const q27 = getAnswer(TRIAGE_QUESTION_ID.RIGHT_LOCATION);
  const q28 = getAnswer(TRIAGE_QUESTION_ID.BOX_UNATTENDED);
  const q07 = getAnswer(TRIAGE_QUESTION_ID.POST_DISCHARGE_OR_EMERGENCY);
  const q16 = getAnswer(TRIAGE_QUESTION_ID.INSTACART_REPLACEMENT);

  const lessThan50 = q34 === 'Less than 50% of the delivery';
  const moreThan50 = q34 === 'More than 50% of the delivery';

  const show55a = lessThan50;
  const show56a = lessThan50 && q55a === 'Yes';
  const show57 = show56a && q08 === 'Yes';
  const show57aTextbox = show57 && q38 === 'Yes';
  const show55b = moreThan50;
  /** 35 (7-day override) only when 37=Yes. When 37=No, skip to 27. */
  const show56b = moreThan50 && q37 === 'Yes';

  const canShow58 =
    (lessThan50 && q55a === 'No') ||
    (lessThan50 && q55a === 'Yes' && q08 === 'No') ||
    (lessThan50 && q55a === 'Yes' && q08 === 'Yes' && q38) ||
    (moreThan50 && q37 === 'No') ||
    (moreThan50 && q37 === 'Yes' && q35);

  return (
    <>
      <FormGroup grouped>
        <label>{QUESTIONS[TRIAGE_QUESTION_ID.RECEIVED_DAMAGE_TYPE].questionText}</label>
        {QUESTIONS[TRIAGE_QUESTION_ID.RECEIVED_DAMAGE_TYPE].options.map((option) => (
          <FormField
            key={option}
            label={option}
            control="input"
            type="radio"
            name="issueType"
            checked={q05b === option}
            onChange={() => handleQuestion(TRIAGE_QUESTION_ID.RECEIVED_DAMAGE_TYPE, option)}
          />
        ))}
      </FormGroup>

      {q05b && (
        <FormGroup grouped>
          <label>{QUESTIONS[TRIAGE_QUESTION_ID.PERCENT_IMPACTED].questionText}</label>
          {QUESTIONS[TRIAGE_QUESTION_ID.PERCENT_IMPACTED].options.map((option) => (
            <FormField
              key={option}
              label={option}
              control="input"
              type="radio"
              name="percentageImpacted"
              checked={q34 === option}
              onChange={() => handleQuestion(TRIAGE_QUESTION_ID.PERCENT_IMPACTED, option)}
            />
          ))}
        </FormGroup>
      )}

      {show55a && (
        <FormGroup grouped>
          <label>{QUESTIONS[TRIAGE_QUESTION_ID.IS_MF_ORDER].questionText}</label>
          {QUESTIONS[TRIAGE_QUESTION_ID.IS_MF_ORDER].options.map((option) => (
            <FormField
              key={option}
              label={option}
              control="input"
              type="radio"
              name="mfOrder"
              checked={q55a === option}
              onChange={() => handleQuestion(TRIAGE_QUESTION_ID.IS_MF_ORDER, option)}
            />
          ))}
        </FormGroup>
      )}

      {show56a && (
        <FormGroup grouped>
          <label>{QUESTIONS[TRIAGE_QUESTION_ID.BONUS_PACK].questionText}</label>
          {QUESTIONS[TRIAGE_QUESTION_ID.BONUS_PACK].options.map((option) => (
            <FormField
              key={option}
              label={option}
              control="input"
              type="radio"
              name="bonusPack"
            checked={q08 === option}
            onChange={() => handleQuestion(TRIAGE_QUESTION_ID.BONUS_PACK, option)}
            />
          ))}
        </FormGroup>
      )}

      {show57 && (
        <FormGroup grouped>
          <label>{QUESTIONS[TRIAGE_QUESTION_ID.VENDOR_REFUND].questionText}</label>
          {QUESTIONS[TRIAGE_QUESTION_ID.VENDOR_REFUND].options.map((option) => (
            <FormField
              key={option}
              label={option}
              control="input"
              type="radio"
              name="refund"
            checked={q38 === option}
            onChange={() => handleQuestion(TRIAGE_QUESTION_ID.VENDOR_REFUND, option)}
            />
          ))}
        </FormGroup>
      )}

      {show57aTextbox && (
        <FormGroup grouped>
          <label>{QUESTIONS[TRIAGE_QUESTION_ID.DAMAGED_ITEMS_NOTE].questionText}</label>
          <TextArea
            value={textInputs[TRIAGE_QUESTION_ID.DAMAGED_ITEMS_NOTE] || ''}
            onChange={(e) => handleTextInput(TRIAGE_QUESTION_ID.DAMAGED_ITEMS_NOTE, e.target.value)}
            placeholder="List damaged items..."
            style={{ minHeight: 80 }}
          />
        </FormGroup>
      )}

      {show55b && (
        <FormGroup grouped>
          <label>{QUESTIONS[TRIAGE_QUESTION_ID.WILL_REPLACE_ORDER].questionText}</label>
          {QUESTIONS[TRIAGE_QUESTION_ID.WILL_REPLACE_ORDER].options.map((option) => (
            <FormField
              key={option}
              label={option}
              control="input"
              type="radio"
              name="replacing"
              checked={q37 === option}
              onChange={() => handleQuestion(TRIAGE_QUESTION_ID.WILL_REPLACE_ORDER, option)}
            />
          ))}
        </FormGroup>
      )}

      {show56b && (
        <FormGroup grouped>
          <label>{QUESTIONS[TRIAGE_QUESTION_ID.SEVEN_DAY_OVERRIDE].questionText}</label>
          {QUESTIONS[TRIAGE_QUESTION_ID.SEVEN_DAY_OVERRIDE].options.map((option) => (
            <FormField
              key={option}
              label={option}
              control="input"
              type="radio"
              name="override"
              checked={q35 === option}
              onChange={() => handleQuestion(TRIAGE_QUESTION_ID.SEVEN_DAY_OVERRIDE, option)}
            />
          ))}
        </FormGroup>
      )}

      {canShow58 && (
        <>
          <FormGroup grouped>
            <label>{QUESTIONS[TRIAGE_QUESTION_ID.RIGHT_LOCATION].questionText}</label>
            {QUESTIONS[TRIAGE_QUESTION_ID.RIGHT_LOCATION].options.map((option) => (
              <FormField
                key={option}
                label={option}
                control="input"
                type="radio"
                name="rightLocation"
                checked={q27 === option}
                onChange={() => handleQuestion(TRIAGE_QUESTION_ID.RIGHT_LOCATION, option)}
              />
            ))}
          </FormGroup>

          {q27 && (
            <FormGroup grouped>
              <label>{QUESTIONS[TRIAGE_QUESTION_ID.BOX_UNATTENDED].questionText}</label>
              {QUESTIONS[TRIAGE_QUESTION_ID.BOX_UNATTENDED].options.map((option) => (
                <FormField
                  key={option}
                  label={option}
                  control="input"
                  type="radio"
                  name="unattended"
                  checked={q28 === option}
                  onChange={() => handleQuestion(TRIAGE_QUESTION_ID.BOX_UNATTENDED, option)}
                />
              ))}
            </FormGroup>
          )}

          {q28 && (
            <FormGroup grouped>
              <label>{QUESTIONS[TRIAGE_QUESTION_ID.POST_DISCHARGE_OR_EMERGENCY].questionText}</label>
              {QUESTIONS[TRIAGE_QUESTION_ID.POST_DISCHARGE_OR_EMERGENCY].options.map((option) => (
                <FormField
                  key={option}
                  label={option}
                  control="input"
                  type="radio"
                  name="reship"
                  checked={q07 === option}
                  onChange={() => handleQuestion(TRIAGE_QUESTION_ID.POST_DISCHARGE_OR_EMERGENCY, option)}
                />
              ))}
            </FormGroup>
          )}

          {q07 && (
            <FormGroup grouped>
              <label>{QUESTIONS[TRIAGE_QUESTION_ID.INSTACART_REPLACEMENT].questionText}</label>
              {QUESTIONS[TRIAGE_QUESTION_ID.INSTACART_REPLACEMENT].options.map((option) => (
                <FormField
                  key={option}
                  label={option}
                  control="input"
                  type="radio"
                  name="instacart"
                  checked={q16 === option}
                  onChange={() => handleQuestion(TRIAGE_QUESTION_ID.INSTACART_REPLACEMENT, option)}
                />
              ))}
            </FormGroup>
          )}
        </>
      )}
    </>
  );
};
